import React, { useMemo, useState } from "react";

import clsx from "clsx";
import { fromPairs, reverse, sortBy } from "lodash";
import { useToasts } from "react-toast-notifications";

import NorthbeamLogo from "@assets/northbeam-logo.svg";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { HTMLTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { NORTHBEAM_BREAKDOWNS } from "@features/sales-v3/utils/breakdown-comparator";
import { BULK_ADD_LABELS, REMOVE_ALL_LABELS } from "@gql/manual-labels";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type BulkAddLabelsToAdObject,
  type BulkAddLabelsToAdObjectVariables,
  type GetCampaign_me_adObject,
  type GetCampaign_me_adObject_labelsDigest,
  type RemoveAllLabels,
  type RemoveAllLabelsVariables,
} from "@north-beam/nb-client-side";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";
import {
  LABEL_KEY_VALIDATIONS,
  LABEL_VALUE_VALIDATIONS,
} from "@pages/objects/label-editor";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import Spinner from "@shared/spinner";

import { type Level } from "../../../types";
import { ColumnMenu } from "../../shared";

import { BreakdownSelector } from "./breakdown-selector";
import { RowToDelete } from "./row-to-delete";
import { Tooltip } from "./tooltip";
import { WarningIcon } from "./warning-icon";

type SortColumn = "key" | "value";
type SortDirection = "asc" | "desc";

function sortLabels(
  labels: GetCampaign_me_adObject_labelsDigest[],
  column: SortColumn,
  direction: SortDirection,
) {
  const byCol = sortBy(labels, [column]);
  return direction === "desc" ? byCol : reverse(byCol);
}

function getBreakdownOptions(
  breakdownConfigsByKey: Record<string, CategoricalBreakdownConfig>,
  key: string,
) {
  const options = breakdownConfigsByKey[key]?.choices ?? [];
  return options.filter((e) => e.value !== "");
}

function validateLabel(value: string, existingLabels: string[]) {
  if (existingLabels.includes(value)) {
    return { success: false, message: "Label key is already present" };
  }

  for (const { message, triggered } of LABEL_KEY_VALIDATIONS) {
    if (triggered(value)) {
      return { success: false, message };
    }
  }

  return { success: true, message: null };
}

function validateValue(value: string) {
  for (const { message, triggered } of LABEL_VALUE_VALIDATIONS) {
    if (triggered(value)) {
      return { success: false, message };
    }
  }

  return { success: true, message: null };
}

interface Props {
  adObject: GetCampaign_me_adObject;
  adObjectLevel: Level;
  onRequestReload(): Promise<void>;
}

export const BreakdownLabels = ({
  adObject,
  adObjectLevel,
  onRequestReload,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const { addToast } = useToasts();

  const [sortColumn, setSortColumn] = useState<SortColumn>("key");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [rowsToDelete, setRowsToDelete] = useState<string[]>([]);
  const [rowsUpdatedValues, setRowsUpdatedValues] = useState<
    { key: string; value: string }[]
  >([]);

  const [bulkAddLabels] = useNorthbeamMutation<
    BulkAddLabelsToAdObject,
    BulkAddLabelsToAdObjectVariables
  >(BULK_ADD_LABELS, { useCpuIntensive: true });

  const [removeAllLabels] = useNorthbeamMutation<
    RemoveAllLabels,
    RemoveAllLabelsVariables
  >(REMOVE_ALL_LABELS, { useCpuIntensive: true });

  const canBeReset = useMemo(
    () => adObject.labelsDigest?.some((e) => e.canDelete),
    [adObject],
  );

  const sortedLabels = useMemo(() => {
    return sortLabels(adObject.labelsDigest!, sortColumn, sortDirection);
  }, [adObject, sortColumn, sortDirection]);

  const breakdownConfigsByKey = useMemo(
    () => fromPairs(breakdownConfigs.map((e) => [e.key, e])),
    [breakdownConfigs],
  );

  const isSaveButtonDisabled = useMemo(() => {
    for (const updatedLabel of rowsUpdatedValues) {
      const labelDigest = adObject.labelsDigest!.find(
        (e) => e.key === updatedLabel.key,
      );

      if (labelDigest) {
        if (labelDigest.value !== updatedLabel.value) {
          return false;
        }
      } else {
        if (updatedLabel.key && updatedLabel.value) {
          return false;
        }
      }
    }

    return true;
  }, [adObject, rowsUpdatedValues]);

  const newRowsUpdatedValues = useMemo(() => {
    const labelKeys = adObject.labelsDigest!.map((e) => e.key);
    return rowsUpdatedValues.filter((e) => !labelKeys.includes(e.key));
  }, [adObject, rowsUpdatedValues]);

  const showNotification = () =>
    addToast(
      "Recalculating Labels! It may take a few minutes before new labels are available " +
        "on report pages",
      { appearance: "info" },
    );

  const applySort = (column: SortColumn) => {
    const newSortDirection = sortDirection === "asc" ? "desc" : "asc";
    prettyLogEventPage({
      domain: "Flyout",
      action: "onClick",
      additionalActionInfo: `Flyout breakdown labels sort ${column} ${newSortDirection}`,
      data: {
        pageContext: { column, sortDirection: newSortDirection },
      },
    });

    setSortDirection(newSortDirection);
    setSortColumn(column);
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold m-0">Breakdown labels</h3>
        {!isEditMode && (
          <TertiaryButton
            className="text-xs font-bold tracking-wide"
            onClick={() => {
              const labels =
                adObject.labelsDigest?.map((e) => ({
                  key: e.key,
                  value: e.value,
                })) ?? [];

              prettyLogEventPage({
                domain: "Flyout",
                action: "onClick",
                additionalActionInfo: "Flyout breakdown labels recategorize",
                data: {
                  pageContext: { labels },
                },
              });

              setRowsUpdatedValues(labels);
              setIsEditMode(true);
            }}
          >
            RECATEGORIZE
          </TertiaryButton>
        )}
      </div>
      {isEditMode && (
        <div>
          <div className="flex text-sm mb-3">
            <div className="bg-sun+1 rounded-s-lg flex items-center justify-center w-9">
              <i className="fa-regular fa-sharp fa-warning text-white" />
            </div>
            <div className="flex-1 p-2 pr-3 text-[#996800] bg-[#FFF4d3] rounded-e-lg">
              Recategorizing can result in inaccurate reporting
            </div>
          </div>
          {canBeReset && (
            <div className="mb-3 flex items-center justify-end">
              {isResetting && (
                <Spinner colorClassname="text-interstellar-1 mx-2 my-1.5" />
              )}
              {!isResetting && (
                <TertiaryButton
                  isDisabled={isSaving || isRemoving}
                  className="text-xs font-bold tracking-wide"
                  onClick={async () => {
                    prettyLogEventPage({
                      domain: "Flyout",
                      action: "onClick",
                      additionalActionInfo:
                        "Flyout breakdown labels reset to original categorization",
                    });

                    setIsResetting(true);
                    setRowsToDelete([]);
                    await removeAllLabels({ variables: { id: adObject.id } });
                    await onRequestReload();
                    setIsEditMode(false);
                    setIsResetting(false);
                    setRowsUpdatedValues([]);
                    showNotification();
                  }}
                >
                  <i className="fa-regular fa-sharp fa-undo font-bold mr-1" />
                  RESET TO ORIGINAL CATEGORIZATION
                </TertiaryButton>
              )}
            </div>
          )}
        </div>
      )}
      <table className="border border-separate border-spacing-0 w-full rounded-lg">
        <thead className="text-xs">
          <tr className="h-10">
            <th className="border-b border-b-gray-3 pl-2 w-1/2">
              <div className="flex items-center">
                <div className="flex-1">Breakdown</div>
                {sortColumn === "key" && (
                  <div
                    className={clsx(
                      "flex justify-center items-center text-primary bg-cosmic-3",
                      "rounded w-6 h-6 ml-2 hover:cursor-pointer",
                    )}
                    onClick={() => applySort("key")}
                  >
                    <i
                      className={clsx("fa-regular fa-sharp", {
                        "fa-arrow-up": sortDirection === "asc",
                        "fa-arrow-down": sortDirection === "desc",
                      })}
                    />
                  </div>
                )}
                <ColumnMenu
                  onSortAsc={() => applySort("key")}
                  onSortDesc={() => applySort("key")}
                />
                <div className="border-r h-[24px] -mr-[1px] ml-2" />
              </div>
            </th>
            <th className="border-b border-b-gray-3 px-2">
              <div className="flex items-center">
                <div className="flex-1">Value</div>
                {sortColumn === "value" && (
                  <div
                    className={clsx(
                      "flex justify-center items-center text-cosmic bg-cosmic-3",
                      "rounded w-6 h-6 ml-2 cursor-pointer",
                    )}
                    onClick={() => applySort("value")}
                  >
                    <i
                      className={clsx("fa-regular fa-sharp", {
                        "fa-arrow-up": sortDirection === "asc",
                        "fa-arrow-down": sortDirection === "desc",
                      })}
                    />
                  </div>
                )}
                <ColumnMenu
                  onSortAsc={() => applySort("value")}
                  onSortDesc={() => applySort("value")}
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedLabels.map((e, i) => {
            const isLastRow = i === sortedLabels.length - 1;

            if (rowsToDelete.includes(e.key)) {
              return (
                <RowToDelete
                  key={e.key}
                  label={e}
                  adObject={adObject}
                  isLastRow={isLastRow}
                  onRemoving={() => setIsRemoving(true)}
                  onRemoved={async () => {
                    await onRequestReload();
                    setRowsToDelete([]);
                    setRowsUpdatedValues(
                      rowsUpdatedValues.filter((v) => v.key !== e.key),
                    );
                    showNotification();
                    setIsRemoving(false);
                    setIsEditMode(false);
                  }}
                  onCancel={() => setRowsToDelete([])}
                />
              );
            } else {
              return (
                <tr
                  key={e.key}
                  className={clsx("h-10", {
                    "bg-cosmic-5": i % 2 === 0,
                    "rounded-b-lg": isLastRow,
                  })}
                >
                  <td
                    className={clsx("border-r px-2", {
                      "border-b": !isLastRow,
                      "border-b-0 rounded-bl-lg": isLastRow,
                    })}
                  >
                    <div className="flex items-center">
                      {!isEditMode && NORTHBEAM_BREAKDOWNS.includes(e.key) && (
                        <div
                          className={clsx(
                            "bg-northbeam-icon rounded-full border border-solid",
                            "mr-2 p-[3px] w-6 h-6",
                          )}
                        >
                          <img
                            className="w-full h-full"
                            alt="northbeam-logo"
                            src={NorthbeamLogo}
                          />
                        </div>
                      )}
                      <div
                        className={clsx("flex-1 flex items-center", {
                          "text-gray+1": isSaving || isResetting || isRemoving,
                        })}
                      >
                        <span className="flex-1 truncate">{e.key}</span>
                      </div>
                      {isEditMode &&
                        e.canDelete &&
                        !isSaving &&
                        !isResetting &&
                        !isRemoving &&
                        rowsToDelete.length === 0 && (
                          <HTMLTooltip
                            noInfoCircle
                            html={
                              e.resolutionOrder.length > 1
                                ? `Remove this override. The resulting label value will be <b>${e.resolutionOrder[1].value}</b>`
                                : "Delete this label key-value pair"
                            }
                          >
                            <button
                              className={clsx(
                                "flex items-center justify-center text-xs w-6 h-6 ml-1",
                                "rounded-full",
                                {
                                  "hover:bg-white": i % 2 === 0,
                                  "hover:bg-cosmic-4": i % 2 !== 0,
                                },
                              )}
                              onClick={() => {
                                if (!rowsToDelete.includes(e.key)) {
                                  setRowsToDelete([...rowsToDelete, e.key]);
                                }
                              }}
                            >
                              <i
                                className={clsx(
                                  "fa-regular fa-sharp text-mars+1",
                                  {
                                    "fa-trash": e.resolutionOrder.length === 1,
                                    "fa-undo": e.resolutionOrder.length > 1,
                                  },
                                )}
                              />
                            </button>
                          </HTMLTooltip>
                        )}
                    </div>
                  </td>
                  <td
                    className={clsx({
                      "border-b": !isLastRow,
                      "border-b-0 rounded-br-lg": isLastRow,
                      "px-2": !isEditMode,
                    })}
                  >
                    {isEditMode ? (
                      <BreakdownSelector
                        isDisabled={isSaving || isResetting || isRemoving}
                        options={getBreakdownOptions(
                          breakdownConfigsByKey,
                          e.key,
                        )}
                        value={{
                          label:
                            rowsUpdatedValues.find((v) => v.key === e.key)
                              ?.value ?? "",
                          value:
                            rowsUpdatedValues.find((v) => v.key === e.key)
                              ?.value ?? "",
                        }}
                        onChange={(value: any) => {
                          const val = value?.value ?? null;
                          const validationResult = validateValue(val);

                          if (validationResult.success) {
                            setRowsUpdatedValues(
                              rowsUpdatedValues.map((v) =>
                                v.key === e.key ? { ...v, value: val } : v,
                              ),
                            );
                          } else {
                            addToast(validationResult.message, {
                              appearance: "warning",
                            });
                          }
                        }}
                      />
                    ) : e.isManualLabel ? (
                      <WarningIcon
                        resolutionOrder={e.resolutionOrder}
                        level={adObjectLevel}
                      >
                        {e.value}
                      </WarningIcon>
                    ) : (
                      e.value
                    )}
                  </td>
                </tr>
              );
            }
          })}
          {newRowsUpdatedValues.map((e, idx) => {
            const i = idx + adObject.labelsDigest!.length;

            return (
              <tr
                key={e.key}
                className={clsx("h-10", {
                  "bg-cosmic-5": i % 2 === 0,
                })}
              >
                <td className="border-t border-r">
                  <BreakdownSelector
                    isDisabled={isSaving || isResetting || isRemoving}
                    options={breakdownConfigs
                      .map((v) => v.key)
                      .filter(
                        (v) =>
                          !rowsUpdatedValues
                            .filter((k) => k !== e)
                            .map((k) => k.key)
                            .includes(v),
                      )
                      .map((v) => ({ label: v, value: v }))}
                    value={{
                      label: e.key ?? "",
                      value: e.key ?? "",
                    }}
                    onChange={(value: any) => {
                      const val = value?.value ?? "";
                      const validationResult = validateLabel(
                        val,
                        rowsUpdatedValues.map((e) => e.key),
                      );

                      if (validationResult.success) {
                        setRowsUpdatedValues(
                          rowsUpdatedValues.map((v) =>
                            v === e ? { ...v, key: val } : v,
                          ),
                        );
                      } else {
                        addToast(validationResult.message, {
                          appearance: "warning",
                        });
                      }
                    }}
                  />
                </td>
                <td className="border-t">
                  <BreakdownSelector
                    isDisabled={isSaving || isResetting || isRemoving}
                    options={getBreakdownOptions(breakdownConfigsByKey, e.key)}
                    value={{
                      label: e.value ?? "",
                      value: e.value ?? "",
                    }}
                    onChange={(value: any) => {
                      const val = value?.value ?? null;
                      const validationResult = validateValue(val);

                      if (validationResult.success) {
                        setRowsUpdatedValues(
                          rowsUpdatedValues.map((v) =>
                            v === e ? { ...v, value: val } : v,
                          ),
                        );
                      } else {
                        addToast(validationResult.message, {
                          appearance: "warning",
                        });
                      }
                    }}
                  />
                </td>
              </tr>
            );
          })}
          {isEditMode && (
            <tr>
              <td colSpan={2} className="h-10 border-t px-2">
                <TertiaryButton
                  className="text-xs font-bold"
                  isDisabled={isSaving || isResetting || isRemoving}
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Flyout",
                      action: "onClick",
                      additionalActionInfo:
                        "Flyout breakdown labels add new breakdown label",
                    });

                    setRowsUpdatedValues([
                      ...rowsUpdatedValues,
                      { key: "", value: "" },
                    ]);
                  }}
                >
                  <i className="fa-regular fa-sharp fa-circle-plus mr-2" />
                  ADD BREAKDOWN LABEL
                </TertiaryButton>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isEditMode && (
        <div className="mt-3 text-right">
          <SecondaryButton
            className="font-bold mr-2"
            onClick={() => {
              prettyLogEventPage({
                domain: "Flyout",
                action: "onClick",
                additionalActionInfo:
                  "Flyout breakdown labels cancel recategorize",
              });

              setRowsToDelete([]);
              setRowsUpdatedValues([]);
              setIsEditMode(false);
            }}
          >
            CANCEL
          </SecondaryButton>
          <PrimaryButton
            isDisabled={isSaveButtonDisabled || isSaving || isResetting}
            loading={isSaving}
            onClick={async () => {
              prettyLogEventPage({
                domain: "Flyout",
                action: "onClick",
                additionalActionInfo:
                  "Flyout breakdown labels save recategorized changes",
              });

              const labels = rowsUpdatedValues.filter((updatedLabel) => {
                const labelDigest = adObject.labelsDigest!.find(
                  (e) => e.key === updatedLabel.key,
                );

                if (labelDigest) {
                  if (labelDigest.value !== updatedLabel.value) {
                    return true;
                  }
                } else {
                  if (updatedLabel.key && updatedLabel.value) {
                    return true;
                  }
                }

                return false;
              });

              setIsSaving(true);
              setRowsToDelete([]);
              await bulkAddLabels({ variables: { id: adObject.id, labels } });
              await onRequestReload();
              setIsEditMode(false);
              setIsSaving(false);
              setRowsUpdatedValues([]);
              showNotification();
            }}
          >
            SAVE
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};
